import { Capacitor } from '@capacitor/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useSubjectMember, useSubjectUpdate } from '@healthblocks-io/core'
import { t } from '@lingui/macro'
import { Formik } from 'formik'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useRecoilState } from 'recoil'
import { useTheme } from 'styled-components'

import { FormListener } from '@/@bothrs-ui/web/components/forms/FormListener'
import { Input } from '@/@bothrs-ui/web/components/forms/input/Input'
import { FlexColumn, Margin } from '@/@bothrs-ui/web/components/layout'
import { Modal } from '@/@bothrs-ui/web/components/modal/Modal'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { P } from '@/@bothrs-ui/web/components/typography/Body'
import { H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { mixpanel } from '@/mixpanel'
import { localUspSlidesAtom } from '@/modules/app/pages/usp/UspSlides.page'
import { useLogout } from '@/modules/auth/hooks/useLogout'
import { useBiometricsPermission } from '@/modules/biometrics/hooks/useBiometricsPermission'
import { BiometricsAppPermission } from '@/modules/biometrics/state/biometrics.atom'
import { useNavigate } from '@/router-wrapper'
import { useToast } from '../../hooks/useToast'

import type { InheritableModalProps } from '@/@bothrs-ui/web/components/modal/Modal.props'

type DeactivateAccountModalProps = InheritableModalProps & {
  onAction: () => void
}

type FormValues = {
  lastName: string
}

const DeactivateAccountModal = ({ onAction, ...props }: DeactivateAccountModalProps) => {
  // -- State --
  const [abletoDeactivate, setAbleToDeactivate] = useState(false)

  // -- Ref --
  const memberIsUpdated = useRef<boolean>(false)

  // -- Hooks --
  const { showErrorToast } = useToast()
  const subjectMember = useSubjectMember()
  const [{ isLoading: memberIsUpdating, error: memberUpdateHasError }, updateSubject] = useSubjectUpdate()

  const [, setLocalUspSlidesAtom] = useRecoilState(localUspSlidesAtom)

  const theme = useTheme()
  const { logout } = useLogout()
  const navigate = useNavigate()

  const { updateBiometricsPermission } = useBiometricsPermission()

  const confirmationString = useMemo(
    () => subjectMember?.familyName ?? Math.random().toString(36).slice(2, 7),
    [subjectMember?.familyName],
  )

  // Effects
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    if (!memberIsUpdated.current || memberIsUpdating || memberUpdateHasError) {
      return
    }

    logout().then(() => {
      if (Capacitor.isNativePlatform()) {
        // Reset biometrics permission to prompt
        updateBiometricsPermission(BiometricsAppPermission.PROMPT)
      }
      navigate('/login')
      setLocalUspSlidesAtom(false)
      onAction()
    })
  }, [
    memberIsUpdated,
    memberIsUpdating,
    memberUpdateHasError,
    navigate,
    logout,
    setLocalUspSlidesAtom,
    onAction,
    updateBiometricsPermission,
  ])

  // Interaction handlers
  // ------------------------------------------------------------------------- /
  const handleChange = (values: Record<string, unknown>) => {
    const typedValues = values as FormValues
    if (typedValues.lastName.toLocaleLowerCase() === confirmationString.toLocaleLowerCase()) {
      setAbleToDeactivate(true)
    } else {
      setAbleToDeactivate(false)
    }
  }

  const handleDeactivateAccount = async () => {
    if (subjectMember) {
      try {
        // Set status to deactivated and reset onboarding

        updateSubject({
          status: 'deactivated',
          meta: {
            ...subjectMember?.meta,
            onboarding: {
              ...subjectMember?.meta.onboarding,
              completed: false,
            },
            hasAcceptedPrivacyPolicy: {
              value: false,
              date: new Date().toJSON(),
            },
            hasConfirmedContactInformation: false,
          },
        }).then(() => {
          mixpanel.track('account_deactivate')
          memberIsUpdated.current = true
        })
      } catch {
        showErrorToast({
          message: t({
            id: 'profile.deactivate-account.toast.message',
            comment: 'Deactivate account error toast message',
            message: 'Er ging iets mis, probeer het nog eens',
          }),
        })
      }
    }
  }

  // -- Render --
  return (
    <Modal {...props}>
      <FlexColumn alignContent='center'>
        <FontAwesomeIcon icon={['fal', 'exclamation-triangle']} fontSize={24} />

        <Margin mt={theme.UI.Spacing.Space5} />

        <H3 textAlign='center' color={theme.Colors.Neutrals.Darkest}>
          {t({
            id: 'profile.account-settings.deactivate-account',
            comment: 'Deactivate account',
            message: 'Account deactiveren',
          })}
        </H3>

        <P type='BodyLargeRegular' textAlign='center' color={theme.Colors.Neutrals.Darker}>
          {t({
            id: 'profile.deactivate-account-modal.message',
            comment: 'Deactivate account message',
            message:
              'Als je je account deactiveert, verlies je toegang tot je gegevens en persoonlijke instellingen. Je kan je account later opnieuw activeren. Je hebt dan opnieuw toegang tot al je gegevens.',
          })}
        </P>
        <P type='BodyLargeRegular' textAlign='center' color={theme.Colors.Neutrals.Darker}>
          {t({
            id: 'profile.deactivate-account-modal.instructions',
            comment: 'Deactivate account input instructions',
            message: 'Typ hieronder je achternaam om te bevestigen: {lastname}',
            values: {
              lastname: confirmationString,
            },
          })}
        </P>

        <Formik initialValues={{ lastName: '' }} onSubmit={() => undefined}>
          <>
            <FormListener onChange={handleChange} fields={[['lastName']]} />
            <Input name='lastName' placeholder={confirmationString} />
          </>
        </Formik>

        <Button
          variant='PrimaryWarning'
          onClick={handleDeactivateAccount}
          disabled={!abletoDeactivate}
          isLoading={memberIsUpdating}
        >
          {t({
            id: 'profile.account-settings.deactivate-account',
            comment: 'Deactivate account',
            message: 'Account deactiveren',
          })}
        </Button>
        <Margin mt={theme.UI.Spacing.Space2} />
        <Button variant='Tertiary' onClick={props.onClose} disabled={memberIsUpdating}>
          {t({
            id: 'globals.cancel',
            comment: 'Global for cancel',
            message: 'Annuleer',
          })}
        </Button>
      </FlexColumn>
    </Modal>
  )
}

export { DeactivateAccountModal }
